import { IonCard } from '@ionic/react'
import { Report } from '@vae/server/dist/client'
import dayjs from 'dayjs'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ModeSwitch, { ModeOption } from '../../../components/ModeSwitch'
import { useAverages } from '../../../contexts/hooks/useAverages'
import { useConsumptionReports } from '../../../contexts/hooks/useConsumptionReports'
import useUser from '../../../contexts/hooks/useUser'
import { ModalContext } from '../../../contexts/ModalContext'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import { trpc } from '../../../modules/trpc'
import ModalWrapper from '../../../wrappers/modalWrapper/ModalWrapper'
import ReportCard from './components/ReportCard'

const MonthlyReport: React.FC = () => {
	const { t } = useTranslation('pages_monthlyReport')

	const { activeReportId } = useContext(ModalContext)
	const object = useContext(ObjectContext)

	const [activeReport, setActiveReport] = useState<Report | null | undefined>(null)
	const [previousReport, setPreviousReport] = useState<Report | null>(null)
	const [selectedCard, setSelectedCard] = useState<string>('general')

	const { user } = useUser()
	const reports = useConsumptionReports(object.id)
	const averages = useAverages()

	const options: ModeOption<string>[] = [
		{ mode: 'general', label: t('yleistilanne') },
		{ mode: 'appliances', label: t('kodinkoneet') },
		// ...(object.heatingType === 'electric' ? [{ mode: 'heating', label: t('lammitys') }] : []),
	]

	useEffect(() => {
		if (!reports.data) return
		const foundReport = reports.data.find(report => report.id === activeReportId)
		const processedReport = foundReport
			? { ...foundReport, lastUpdated: new Date(foundReport.lastUpdated) }
			: null
		setActiveReport(processedReport)
		const activeFrom = dayjs(foundReport?.from)
		const sameMonthPrevYear = reports.data.find(
			r =>
				dayjs(r.from).month() === activeFrom.month() &&
				dayjs(r.from).year() === activeFrom.year() - 1
		)
		setPreviousReport(
			sameMonthPrevYear
				? { ...sameMonthPrevYear, lastUpdated: new Date(sameMonthPrevYear.lastUpdated) }
				: null
		)
	}, [activeReportId, reports.data])

	const utils = trpc.useContext()
	const { mutate, isLoading } = trpc.objects.updateReport.useMutation({
		onSuccess: () => {
			utils.objects.getReports.invalidate({ objectId: object.id })
		},
	})

	if (!activeReport) return null

	const year = activeReport.from.split('-')[0]
	const monthsName = dayjs(activeReport.from).format('MMMM')
	const capitalizedMonth = monthsName.charAt(0).toUpperCase() + monthsName.slice(1)

	const appBarOptions = {
		onRefetchClick: async () => {
			mutate({
				id: activeReport.id,
				data: {
					from: activeReport.from,
					to: activeReport.to,
					objectId: activeReport.objectId,
					timeframe:
						dayjs(activeReport.from).diff(activeReport.to, 'month', true) < 2
							? 'monthly'
							: 'yearly',
					userId: user!.id,
				},
			})
			await new Promise(resolve => {
				const checkLoading = () => {
					if (!isLoading) {
						resolve(undefined)
					} else {
						setTimeout(checkLoading, 50)
					}
				}
				checkLoading()
			})
		},
		refetchAlertText: {
			header: t('fetchUpdateHeader'),
			text: t('fetchUpdateText'),
			confirmText: t('fetchUpdateConfirm'),
			cancelText: t('fetchUpdateCancel'),
		},
		label: `${capitalizedMonth} ${year}`,
	}

	return (
		<ModalWrapper
			appBarOptions={
				// Show refresh button only if the report is more than 2 hours old
				dayjs(activeReport.lastUpdated).diff(dayjs(), 'hours') < -2
					? appBarOptions
					: { label: `${capitalizedMonth} ${year}` }
			}
		>
			<div className='main-card bg-white dark:bg-surface-dark'>
				<div className='info'>
					<div className='name text-text-dark dark:text-white'>{t('kokonaiskulutus')}</div>
					<div className='amount'>
						<span className='value text-brand-primary-100-light dark:text-brand-primary-100-dark'>
							{activeReport.totalConsumption}
						</span>
						<span className='unit text-text-grey-200 dark:text-text-grey-100'>kWh</span>
					</div>
				</div>
			</div>

			<div className='mt-4 -mb-4'>
				<ModeSwitch
					options={options}
					currentMode={selectedCard}
					onModeChange={setSelectedCard}
					border
				/>
			</div>

			<IonCard className='container bg-white dark:bg-surface-dark'>
				<ReportCard
					cardName={selectedCard}
					observedReport={activeReport}
					previousReport={previousReport}
					averages={averages.data}
				/>
			</IonCard>
		</ModalWrapper>
	)
}

export default MonthlyReport
